<template>
  <div>
    <a-form
      ref="formRef"
      layout="vertical"
      :model="form"
    >
      <a-card :bordered="false">
        <a-row :gutter="16">
          <a-col :span="24">
            <a-form-item
              ref="content"
              name="content"
              :auto-link="false"
              :rules="{
                type: 'string',
                required: true,
                message: $t('okr.formRules.required.keyResult'),
                trigger: 'blur'
              }"
            >
              <MentionTextarea
                v-model:value="form.content"
                :form-item-ref="$refs.content"
                :mentioned-user-ids="form.mentionUserIds"
                search-range="ALL"
                :placeholder="$t('okr.editKeyResultDialog.contentPlaceholder')"
              />
              <template #label>
                <a-space>
                  <KeyResultIcon :size="16" />
                  {{ $t('shared.keyResult') }}
                </a-space>
              </template>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              :label="$t('shared.startDay')"
              name="startDay"
            >
              <a-date-picker
                v-model:value="form.startDay"
                class="date-picker"
                :disabled-date="disabledDateForStartDay"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              :label="$t('shared.finishDay')"
              name="finishDay"
            >
              <a-date-picker
                v-model:value="form.finishDay"
                class="date-picker"
                :disabled-date="disabledDateForFinishDay"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-form-item
              ref="indicator"
              name="indicator"
              :auto-link="false"
            >
              <MentionTextarea
                v-model:value="form.indicator"
                :form-item-ref="$refs.indicator"
                :mentioned-user-ids="form.mentionUserIds"
                :placeholder="$t('okr.editKeyResultDialog.targetPlaceholder')"
              />
              <template #label>
                <a-space>
                  {{ $t('shared.indicator') }}
                </a-space>
              </template>
            </a-form-item>
          </a-col>
          <a-col
            v-if="isEditing"
            :span="24"
          >
            <a-form-item
              :label="$t('shared.progress')"
              name="progress"
              :rules="{
                type: 'number',
                required: true,
                message: $t('okr.formRules.required.shared'),
                trigger: 'blur'
              }"
            >
              <a-input-number
                v-model:value="form.progress"
                name="progress"
                :min="0"
                :max="100"
                :step="5"
                :formatter="value => `${value}%`"
                :parser="value => value.replace('%', '')"
                :style="{ width: '100%' }"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-form-item :label="$t('weekly.collaborativeUser')">
              <a-select
                v-model:value="form.collaborators"
                mode="multiple"
                :placeholder="$t('weekly.collaborativeUser')"
                :filter-option="false"
                :options="users"
                @search="fetchUser"
              >
                <template
                  v-if="fetchUserLoading"
                  #notFoundContent
                >
                  <a-spin size="small" />
                </template>
              </a-select>
            </a-form-item>
          </a-col>
          <!-- <a-col
            v-if="isEditing"
            :span="12"
          >
            <a-form-item
              :label="$t('shared.state')"
              name="stateId"
            >
              <a-select
                v-model:value="form.stateId"
              >
                <a-select-option
                  v-for="option in stateOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ $t(`okr.stateOptions.${option.value - 1}`) }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col> -->
        </a-row>
      </a-card>
    </a-form>
    <FormAction
      :loading="loading"
      :can-close="canClose"
      @cancel="$emit('cancel')"
      @save="onSave()"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, toRefs, unref, watch, toRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'

import STATE_OPTIONS from '@/constants/state-options'
import { replaceStr2Html } from '@/utils/line-break'
import { createKeyResult, updateKeyResult } from '@/api/okr'
import { editKeyResultAuth, removeKeyResultAuth } from '@/api/auth'
import useMention from '@/composables/use-mention'
import KeyResultIcon from '@/components/KeyResultIcon'
import FormAction from './FormAction'
import MentionTextarea from '@/components/MentionTextarea'
import { calculateStatus } from '@/utils/calculateStatus'
import { debounce } from 'lodash'
import { searchUserFromAll } from '@/api/user'

export default {
  components: {
    KeyResultIcon,
    FormAction,
    MentionTextarea
  },
  props: {
    objectiveId: {
      type: Number,
      default: undefined
    },
    initForm: {
      type: Object,
      default: () =>
        reactive({
          content: '',
          indicator: '',
          startDay: undefined,
          finishDay: undefined,
          progress: 0,
          stateId: 1,
          confidenceIndex: 5,
          mentionUserIds: [],
          collaborators: []
        })
    },
    canClose: {
      type: Boolean,
      required: true
    }
  },
  emits: [
    'update:can-close',
    'key-result-created',
    'key-result-saved',
    'cancel'
  ],
  setup (props, { emit }) {
    const { t } = useI18n()
    const isEditing = computed(() => !props.objectiveId)
    const { getFormattedUserIds, getAddUserIds, getRmUserIds } = useMention()
    const formRef = ref()
    const { initForm, objectiveId } = toRefs(props)
    const form = unref(initForm)
    const loading = ref(false)
    const originalMentionUserIds = [...toRaw(form.mentionUserIds)]
    watch(form, () => {
      emit('update:can-close', false)
    })

    watch(
      [
        () => form.startDay,
        () => form.finishDay,
        () => form.progress
      ],
      ([startDay, finishDay, progress]) => {
        if (form.stateId === 4) {
          return
        }
        form.stateId =
          calculateStatus(startDay, finishDay, progress) || form.stateId
      }
    )

    function onSave () {
      formRef.value
        .validate()
        .then(() => save())
        .catch(() => {})
    }

    function save () {
      loading.value = true
      const data = {
        content: replaceStr2Html(form.content),
        indicator: replaceStr2Html(form.indicator || ''),
        start_day: form?.startDay?.unix(),
        finish_day: form?.finishDay?.unix(),
        // confidence_index: form.confidenceIndex,
        progress: form.progress,
        state_id: form.stateId,
        mention_user_ids: getFormattedUserIds(form.content, form.mentionUserIds),
        collaborators: [[6, 0, form.collaborators]]
      }
      const addUserIds = getAddUserIds(originalMentionUserIds, data.mention_user_ids[0][2])
      const rmUserIds = getRmUserIds(originalMentionUserIds, data.mention_user_ids[0][2])
      const requestPromise = objectiveId.value
        ? createKeyResult(objectiveId.value, data)
        : updateKeyResult(form.id, data)

      requestPromise.then(keyResultId => {
        addAuth(form.id || keyResultId, addUserIds)
        removeAuth(form.id || keyResultId, rmUserIds)
        loading.value = false
        message.success(t('okr.editKeyResultDialog.successMessage'))
        if (objectiveId.value) {
          emit('key-result-created', keyResultId)
        } else {
          emit('key-result-saved')
        }
      }).catch(e => {
        loading.value = false
      })
    }

    function addAuth (id, addUserIds) {
      if (addUserIds.length === 0) {
        return
      }
      editKeyResultAuth(id, addUserIds).then(res => {
        console.log(res)
      })
    }

    function removeAuth (id, rmUserIds) {
      if (rmUserIds.length === 0) {
        return
      }
      removeKeyResultAuth(id, rmUserIds).then(res => {
        console.log(res)
      })
    }

    function disabledDateForFinishDay (date) {
      return form.startDay && date.isBefore(form.startDay)
    }

    function disabledDateForStartDay (date) {
      return form.finishDay && date.isAfter(form.finishDay)
    }
    const fetchUserLoading = ref(false)
    const users = ref([])
    if (form.collaborators.length > 0) {
      users.value = form.collaborators.map(item => ({
        value: item.id,
        label: item.name
      }))
      form.collaborators = form.collaborators.map(item => item.id)
    }
    const fetchUser = debounce(value => {
      fetchUserLoading.value = true
      searchUserFromAll(value).then(usersRaw => {
        users.value = usersRaw.map(item => ({
          value: item.uid,
          label: item.name
        }))
        fetchUserLoading.value = false
      })
    }, 300)
    return {
      isEditing,
      form,
      formRef,
      stateOptions: STATE_OPTIONS,
      onSave,
      loading,
      disabledDateForFinishDay,
      disabledDateForStartDay,
      fetchUser,
      users,
      fetchUserLoading
    }
  }
}
</script>

<style lang="less" scoped>
:deep(.ant-card:first-child) {
  margin-top: 24px;
}

:deep(.ant-card) {
  box-shadow: none !important;
}

:deep(.ant-card-body) {
  padding-top: 0;
  padding-bottom: 0;
}

.date-picker {
  width: 100%;
}
</style>
